import { useMemo } from "react";
import type { CopyNurses } from "@utils/rosterCreate-util";
import type { WantedAdjustmentData } from "@/hooks/roster-craete/useWantedAdjustmentQuery";
import type { ShiftCatalogShift } from "@/services/shift/shiftCatalog";
import type { ShiftLookupMaps } from "@/utils/shift-util";
import {
  aggregateWantedCompliance,
  type WantedComplianceTotals,
} from "@/utils/wanted-compliance";

function calendarDayFromShiftDate(
  shiftDate: string,
  expectedYear: number,
  expectedMonth: number
): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(shiftDate.trim());
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  if (y !== expectedYear || month !== expectedMonth) return null;
  if (!Number.isFinite(day) || day < 1 || day > 31) return null;
  return day;
}

type UseRosterWantedOverlayParams = {
  copyNurses: CopyNurses;
  wantedAdjustmentData: WantedAdjustmentData | undefined;
  year: number | null | undefined;
  month: number | null | undefined;
};

export function useRosterWantedOverlay({
  copyNurses,
  wantedAdjustmentData,
  year,
  month,
}: UseRosterWantedOverlayParams) {
  return useMemo(() => {
    if (!wantedAdjustmentData?.nurses || copyNurses.length === 0) {
      return copyNurses;
    }

    const wantedNursesMap = new Map(
      wantedAdjustmentData.nurses.map((wantedNurse) => [
        wantedNurse.nurse_id,
        wantedNurse,
      ])
    );

    const resolvedYear = year ?? new Date().getFullYear();
    const resolvedMonth = month ?? new Date().getMonth() + 1;
    const daysInMonth = new Date(resolvedYear, resolvedMonth, 0).getDate();

    return copyNurses.map((nurse) => {
      const wantedNurse = wantedNursesMap.get(nurse.nurse_id);
      const wantedEntriesMap: Record<number, string> = {};
      const wantedShiftsTableIdsMap: Record<number, number | null> = {};
      const bannedShiftMap: Record<
        number,
        {
          shiftIds: string[];
          isApplied: boolean;
          source?: string;
          reason?: string | null;
        }
      > = {};
      const appliedFixedDays = new Set<number>();

      wantedNurse?.entries.forEach((entry) => {
        if (!entry.shift_date || !entry.shift_id) return;

        const day = calendarDayFromShiftDate(
          entry.shift_date,
          resolvedYear,
          resolvedMonth
        );
        if (day == null || day < 1 || day > daysInMonth) return;
        if (entry.is_applied === false) return;

        wantedEntriesMap[day] = entry.shift_id;
        appliedFixedDays.add(day);
        wantedShiftsTableIdsMap[day] =
          entry.shifts_table_id != null &&
          Number.isFinite(Number(entry.shifts_table_id))
            ? Number(entry.shifts_table_id)
            : null;
      });

      wantedNurse?.banned_entries.forEach((entry) => {
        if (!entry.shift_date || entry.is_applied === false) return;

        const day = calendarDayFromShiftDate(
          entry.shift_date,
          resolvedYear,
          resolvedMonth
        );
        if (day == null || day < 1 || day > daysInMonth) return;
        if (appliedFixedDays.has(day)) return;

        bannedShiftMap[day] = {
          shiftIds: entry.banned_shift_ids,
          isApplied: true,
          source: entry.source,
          reason: entry.reason,
        };
      });

      return {
        ...nurse,
        wantedEntriesMap,
        wantedShiftsTableIdsMap,
        bannedShiftMap,
      };
    });
  }, [copyNurses, wantedAdjustmentData, year, month]);
}

type UseWantedComplianceTotalsParams = {
  copyNurses: CopyNurses;
  wantedAdjustmentData: WantedAdjustmentData | undefined;
  year: number | null | undefined;
  month: number | null | undefined;
  shiftLookupMaps: ShiftLookupMaps<ShiftCatalogShift> | undefined;
};

export function useWantedComplianceTotals({
  copyNurses,
  wantedAdjustmentData,
  year,
  month,
  shiftLookupMaps,
}: UseWantedComplianceTotalsParams): WantedComplianceTotals | null {
  return useMemo(() => {
    if (!wantedAdjustmentData?.nurses || copyNurses.length === 0) return null;
    if (!shiftLookupMaps) return null;

    const resolvedYear = year ?? new Date().getFullYear();
    const resolvedMonth = month ?? new Date().getMonth() + 1;
    const daysInMonth = new Date(resolvedYear, resolvedMonth, 0).getDate();
    const wantedNursesById = new Map(
      wantedAdjustmentData.nurses.map((nurse) => [nurse.nurse_id, nurse])
    );

    const nursesWithWantedMaps = copyNurses.map((nurse) => {
      const wantedNurse = wantedNursesById.get(nurse.nurse_id);
      const wantedEntriesMap: Record<number, string> = {};
      const bannedShiftMap: Record<
        number,
        { shiftIds: string[]; isApplied: boolean }
      > = {};

      wantedNurse?.entries.forEach((entry) => {
        const day = calendarDayFromShiftDate(
          entry.shift_date,
          resolvedYear,
          resolvedMonth
        );
        if (day == null || day > daysInMonth || entry.is_applied === false) {
          return;
        }
        wantedEntriesMap[day] = entry.shift_id;
      });

      wantedNurse?.banned_entries.forEach((entry) => {
        const day = calendarDayFromShiftDate(
          entry.shift_date,
          resolvedYear,
          resolvedMonth
        );
        if (day == null || day > daysInMonth) return;
        bannedShiftMap[day] = {
          shiftIds: entry.banned_shift_ids,
          isApplied: entry.is_applied !== false,
        };
      });

      return {
        name: nurse.name,
        schedule: nurse.schedule ?? undefined,
        wantedEntriesMap,
        bannedShiftMap,
      };
    });

    return aggregateWantedCompliance(nursesWithWantedMaps, shiftLookupMaps);
  }, [copyNurses, wantedAdjustmentData, year, month, shiftLookupMaps]);
}
